import React, {useState, useCallback} from 'react';
import {BaseConfig} from 'config';
import {JobMenuItem} from 'shared/components/Header/JobMenuItem';
import useUI from 'shared/useUI';
import {SidebarStatus, SidebarBehaviour} from 'store/customer/UISlice';
import Sidebar, {useLeftSidebar} from 'shared/components/Sidebar';
import {SidebarTab} from 'shared/components/Sidebar/SidebarTab';
import {Icon} from 'shared/helpers';
import MenuButton from 'shared/components/MenuButton';
import styled from 'styled-components';
import {useRoomPlannerLeftMenu} from 'components/customer/RoomPlanner/helper/useRoomPlannerLeftMenu';
import {ContextMenu as RoomPlannerContextMenu} from 'components/customer/RoomPlanner/components/Menu/ContextMenu';
import {
    useIsHandheldPortrait,
    useIsMobilePortrait,
    useIsTabletPortrait,
    useIsMobileLandscape,
    useIsTouchDevices,
} from 'shared/helpers/DeviceSize';

export const SidebarLeft = () => {
    const isHandheldPortrait = useIsHandheldPortrait();
    const isMobilePortrait = useIsMobilePortrait();
    const isTablerPortrait = useIsTabletPortrait();
    const isMobileLandscape = useIsMobileLandscape();
    const isTouchDevice = useIsTouchDevices();

    const {isSmallScreenLayout, leftSidebar, leftSidebarBehaviour} = useUI();
    const {toggle} = useLeftSidebar();
    const isOpen = leftSidebar === SidebarStatus.OPEN;
    const [hasVerticalScroll, setHasVerticalScroll] = useState(false);

    const {isRoomPlannerPage} = useRoomPlannerLeftMenu();

    const handleNavRefChange = useCallback((ref: HTMLElement | null) => {
        const navRef = ref;
        if (navRef) {
            const hasVertScroll = navRef.scrollHeight > navRef.clientHeight;

            setHasVerticalScroll(hasVertScroll);
        }
    }, []);

    if (isRoomPlannerPage) {
        // the room planner has no bottom menu. it switches to its own mobile
        // layout on touch phones and tablets in portrait, and keeps this menu
        // everywhere else, narrow desktop windows included
        if (isMobilePortrait || isTablerPortrait) {
            return null;
        }
    } else if (isHandheldPortrait) {
        // these get the floating bottom menu instead
        return null;
    }

    return (
        <Sidebar
            sidebarId="sidebar-left"
            className="sidebar-left"
            anchor="LEFT"
            behaviour={leftSidebarBehaviour}
            status={leftSidebar}
            showOverlays={false} // NOTE: fix this by testing the UI
            partialClose={leftSidebarBehaviour === SidebarBehaviour.TOGGLE}
            partialCloseWidth={
                !isTouchDevice && isSmallScreenLayout
                    ? 45
                    : isMobileLandscape
                      ? 50
                      : 80
            }
            style={
                leftSidebarBehaviour === SidebarBehaviour.TOGGLE
                    ? {position: 'relative'}
                    : undefined
            }>
            <>
                <SidebarTab
                    type="button"
                    $anchor="LEFT"
                    title={isOpen ? 'Collapse menu' : 'Expand menu'}
                    aria-label={isOpen ? 'Collapse menu' : 'Expand menu'}
                    onClick={toggle}>
                    <Icon
                        iconName={
                            isOpen ? 'chevron-left.svg' : 'chevron-right.svg'
                        }
                    />
                </SidebarTab>
                <div className="sidebar-left-content">
                    <MenuButton status={leftSidebar} onClick={toggle} />
                    <NavContainer
                        ref={(ref) => handleNavRefChange(ref)}
                        className="nav-container"
                        style={
                            isSmallScreenLayout &&
                            leftSidebar === SidebarStatus.CLOSED &&
                            hasVerticalScroll
                                ? {
                                      overflowY: 'hidden',
                                      overflowX: 'hidden',
                                      paddingRight: 10,
                                  }
                                : {
                                      overflowX: 'hidden',
                                  }
                        }>
                        <JobMenu
                            jobMenu={BaseConfig.USER_CUSTOMER.jobMenu}
                            isCollapsed={
                                leftSidebar != SidebarStatus.OPEN &&
                                leftSidebarBehaviour === SidebarBehaviour.TOGGLE
                            }
                            $isRoomPlannerPage={!!isRoomPlannerPage}
                        />

                        <RoomPlannerContextMenu />
                    </NavContainer>
                </div>
            </>
        </Sidebar>
    );
};

const NavContainer = styled.section`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 0 0 0 !important;
`;

const JobMenu = styled(JobMenuItem)<{
    $isRoomPlannerPage: boolean;
}>`
    ${({$isRoomPlannerPage}) => {
        if ($isRoomPlannerPage) {
            return `
                display: none;
            `;
        }
    }}
`;
